<!-- Drive — the lap itself, from inside the car. Lights out, steer, brake
     when you need to, cross the line. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" :scroll-y="false" class="drive-content">
      <!-- The cockpit is a game surface: its pads are a controller held with
           both thumbs, drawn over a night scene, not form buttons, so the kit's
           controls do not apply here (data-kit-exempt). -->
      <div class="drive" data-kit-exempt @contextmenu.prevent>
        <canvas ref="canvas" class="drive-canvas" role="img" :aria-label="COPY.canvasLabel" />

        <div class="hud" aria-live="off">
          <div class="hud-cell">
            <span class="hud-label">{{ COPY.position }}</span>
            <span class="hud-value">P{{ s.position }}<small>/{{ s.cars }}</small></span>
          </div>
          <div class="hud-cell hud-cell--mid">
            <span class="hud-label">{{ COPY.lapTime }}</span>
            <span class="hud-value hud-mono">{{ formatLapTime(s.timeMs) }}</span>
          </div>
          <div class="hud-cell hud-cell--end">
            <span class="hud-label">{{ COPY.next }}</span>
            <span class="hud-value">{{ s.corner || '—' }}</span>
          </div>
          <div class="hud-progress" aria-hidden="true"><span :style="{ width: `${s.lapShare * 100}%` }" /></div>
        </div>

        <button type="button" class="leave" @click="leave">{{ COPY.leave }}</button>

        <div v-if="s.phase === 'lights' || lightsOutFlash" class="lights" role="status">
          <div class="lights-row">
            <span v-for="i in 5" :key="i" class="light" :class="{ on: s.phase === 'lights' && i <= s.lights }" />
          </div>
          <p class="lights-text">{{ s.phase === 'lights' ? COPY.wait : COPY.go }}</p>
        </div>

        <p v-if="s.offTrack && s.phase === 'race'" class="warn" role="status">{{ COPY.offTrack }}</p>

        <div v-if="s.phase === 'finished'" class="finish" role="status">{{ COPY.finish }}</div>

        <div class="controls">
          <button
            type="button" class="pad pad--left" :aria-label="COPY.left"
            @pointerdown.prevent="press('left', $event)" @pointerup="release('left')"
            @pointercancel="release('left')" @pointerleave="release('left')"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          </button>
          <div class="pad-col">
            <button
              type="button" class="pad pad--brake" :aria-label="COPY.brake"
              @pointerdown.prevent="press('brake', $event)" @pointerup="release('brake')"
              @pointercancel="release('brake')" @pointerleave="release('brake')"
            >{{ COPY.brake }}</button>
            <button
              type="button" class="pad pad--right" :aria-label="COPY.right"
              @pointerdown.prevent="press('right', $event)" @pointerup="release('right')"
              @pointercancel="release('right')" @pointerleave="release('right')"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonPage, IonContent, onIonViewWillLeave } from '@ionic/vue';
import { prefersReducedMotion } from '../core/ui/index.js';
import { Race, formatLapTime } from '../game/race.js';
import { addLap, findLap, saveLap, storeReady } from '../store.js';
import { bestLap, placeLabel, CAR, TRACK } from '../game/results.js';

const COPY = {
  canvasLabel: 'The view from the driver’s seat: the road ahead, the rivals and the Marina Bay skyline',
  position: 'Position',
  lapTime: 'Lap time',
  next: 'Next',
  leave: 'Leave',
  wait: 'Wait for the lights to go out',
  go: 'Go',
  offTrack: 'Back onto the track',
  finish: 'Chequered flag',
  left: 'Steer left',
  right: 'Steer right',
  brake: 'Brake',
};

const route = useRoute();
const router = useRouter();
const canvas = ref(null);
const s = reactive({ phase: 'lights', lights: 0, timeMs: 0, position: 6, cars: 6, kmh: 0, corner: 'Turn 1', lapShare: 0, offTrack: false });
const lightsOutFlash = ref(false);
const held = reactive({ left: false, right: false, brake: false });
let race = null;

function applyControls() {
  if (!race) return;
  race.setSteer((held.right ? 1 : 0) - (held.left ? 1 : 0));
  race.setBrake(held.brake);
}
function press(which, e) {
  e?.target?.setPointerCapture?.(e.pointerId);
  held[which] = true;
  applyControls();
}
function release(which) {
  held[which] = false;
  applyControls();
}

const KEYS = { ArrowLeft: 'left', a: 'left', A: 'left', ArrowRight: 'right', d: 'right', D: 'right', ArrowDown: 'brake', s: 'brake', S: 'brake', ' ': 'brake' };
function onKey(e) {
  const which = KEYS[e.key];
  if (!which) return;
  e.preventDefault();
  held[which] = e.type === 'keydown';
  applyControls();
}

watch(() => s.phase, (now, before) => {
  if (before === 'lights' && now === 'race') {
    lightsOutFlash.value = true;
    setTimeout(() => { lightsOutFlash.value = false; }, 900);
  }
});

async function finish({ timeMs, position }) {
  await storeReady;
  const previousBest = bestLap.value ? Number(bestLap.value.timeMs) : Infinity;
  const fields = {
    status: timeMs < previousBest ? 'best-lap' : 'track',
    timeMs,
    place: position,
    lapTime: formatLapTime(timeMs),
    position: `P${position}`,
    result: placeLabel(position),
    bestLap: formatLapTime(Math.min(previousBest, timeMs)),
    speed: `${s.kmh} km/h`,
    corner: 'Turn 7',
  };
  let lap = findLap(route.params.id);
  if (lap && Number(lap.id) > 0 && !lap.lapTime) lap = saveLap({ ...lap, ...fields });
  else lap = addLap({ summary: 'Lap of Marina Bay', car: CAR, track: TRACK, controls: 'Steer, brake', ...fields });
  setTimeout(() => router.replace({ name: 'flag', params: { id: lap.id } }), 1400);
}

const onResize = () => race?.resize();

onMounted(() => {
  race = new Race(canvas.value, {
    reducedMotion: prefersReducedMotion(),
    onTick: (st) => Object.assign(s, st),
    onFinish: finish,
  });
  race.start();
  window.addEventListener('keydown', onKey);
  window.addEventListener('keyup', onKey);
  window.addEventListener('resize', onResize);
});

function teardown() {
  race?.stop();
  race = null;
  window.removeEventListener('keydown', onKey);
  window.removeEventListener('keyup', onKey);
  window.removeEventListener('resize', onResize);
}
onIonViewWillLeave(teardown);
onBeforeUnmount(teardown);

const leave = () => router.replace({ name: 'grid' });
</script>

<style scoped>
.drive-content { --background: var(--race-night); }
.drive {
  position: relative; height: 100%; width: 100%; overflow: hidden;
  user-select: none; -webkit-user-select: none; touch-action: none; -webkit-touch-callout: none;
  color: var(--race-text);
}
.drive-canvas { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.hud {
  position: absolute; left: 12%; right: 12%;
  top: calc(env(safe-area-inset-top, 0px) + 6%);
  display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3); border-radius: 12px;
  background: var(--race-glass); backdrop-filter: blur(4px);
}
.hud-cell { display: flex; flex-direction: column; gap: 2px; }
.hud-cell--mid { align-items: center; }
.hud-cell--end { align-items: flex-end; }
.hud-label { font-size: var(--text-xs); letter-spacing: 0.06em; text-transform: uppercase; color: var(--race-muted); }
.hud-value { font-size: var(--text-lg); font-weight: 800; line-height: 1.1; }
.hud-value small { font-size: var(--text-xs); font-weight: 600; color: var(--race-muted); }
.hud-mono { font-variant-numeric: tabular-nums; }
.hud-progress { grid-column: 1 / -1; height: 4px; border-radius: 2px; background: rgba(255,255,255,0.15); overflow: hidden; }
.hud-progress span { display: block; height: 100%; background: var(--race-red); }

.leave {
  position: absolute; left: var(--sp-3); top: calc(env(safe-area-inset-top, 0px) + 6% + 88px);
  min-height: 44px; padding: 0 var(--sp-4); border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.35); background: var(--race-glass); color: var(--race-text); font-weight: 600;
}

.lights {
  position: absolute; left: 50%; top: 30%; transform: translate(-50%, -50%);
  display: grid; justify-items: center; gap: var(--sp-3);
  padding: var(--sp-4); border-radius: 16px; background: rgba(0,0,0,0.75);
}
.lights-row { display: flex; gap: 10px; }
.light { width: 38px; height: 38px; border-radius: 50%; background: var(--race-light-off); border: 3px solid var(--race-black); }
.light.on { background: var(--race-light-on); box-shadow: 0 0 18px var(--race-light-on); }
.lights-text { margin: 0; font-weight: 700; font-size: var(--text-md); }

.warn {
  position: absolute; left: 50%; top: 52%; transform: translateX(-50%); margin: 0;
  padding: var(--sp-2) var(--sp-4); border-radius: 10px; background: var(--race-warn); color: var(--race-black); font-weight: 800;
}
.finish {
  position: absolute; left: 50%; top: 34%; transform: translate(-50%, -50%);
  padding: var(--sp-3) var(--sp-5, 24px); border-radius: 12px; font-size: var(--text-2xl); font-weight: 900;
  color: var(--race-black); background: repeating-conic-gradient(var(--race-white) 0 25%, var(--race-chequer-grey) 0 50%) 0 0 / 24px 24px;
  border: 4px solid var(--race-black);
}

.controls {
  position: absolute; left: 0; right: 0; bottom: calc(env(safe-area-inset-bottom, 0px) + var(--sp-3));
  display: flex; justify-content: space-between; align-items: flex-end; padding: 0 var(--sp-3);
}
.pad {
  min-width: 84px; min-height: 84px; border-radius: 18px;
  border: 2px solid var(--race-pad-edge); background: var(--race-pad); color: var(--race-text);
  display: grid; place-items: center; font-weight: 800; font-size: var(--text-md);
  touch-action: none;
}
.pad:active { background: rgba(255,255,255,0.32); }
.pad svg { width: 40px; height: 40px; fill: none; stroke: currentColor; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
.pad-col { display: grid; gap: var(--sp-3); justify-items: end; }
.pad--brake { min-width: 84px; min-height: 56px; background: rgba(255,59,48,0.35); border-color: rgba(255,120,110,0.8); }
@media (min-width: 700px) {
  .hud { left: 25%; right: 25%; }
}
</style>
